import { AttachmentMetadata } from "@/types/chat"

import { ChatApi } from "./chat-api"
import { buildClientAxios } from "./client-axios"

function extractYouTubeVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()

    // youtu.be short URLs
    if (hostname === "youtu.be" || hostname === "www.youtu.be") {
      const videoId = urlObj.pathname.slice(1).split("/")[0]
      return videoId || null
    }

    // youtube.com URLs
    if (hostname === "youtube.com" || hostname === "www.youtube.com") {
      // Standard watch URLs
      if (urlObj.pathname === "/watch") {
        return urlObj.searchParams.get("v")
      }

      // Embedded URLs
      if (urlObj.pathname.startsWith("/embed/")) {
        return urlObj.pathname.split("/embed/")[1].split("?")[0]
      }

      // Old v/ URLs
      if (urlObj.pathname.startsWith("/v/")) {
        return urlObj.pathname.split("/v/")[1].split("?")[0]
      }

      // Shorts URLs
      if (urlObj.pathname.startsWith("/shorts/")) {
        return urlObj.pathname.split("/shorts/")[1].split("?")[0]
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * @param url
 * @returns Thumbnail URL or null if video ID cannot be extracted
 */
export function getYouTubeThumbnail(url: string): string | null {
  const videoId = extractYouTubeVideoId(url)
  if (!videoId) return null

  // maxresdefault (1280x720)
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
}

/**
 * @param url - Media URL from supported platforms or web page URL
 * @returns Promise that resolves to full AttachmentMetadata or null
 */
export async function getAttachmentMetadata(
  url: string
): Promise<AttachmentMetadata | null> {
  if (!url || typeof url !== "string") {
    console.error("Invalid URL provided:", url)
    return null
  }

  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()

    if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
      const thumbnailUrl = getYouTubeThumbnail(url)
      return {
        thumbnail_url: thumbnailUrl || undefined,
      }
    }

    const chatApi = new ChatApi(buildClientAxios())
    const metadata = await chatApi.fetchAttachmentMetadata(url)

    return metadata
  } catch (error) {
    console.error("Error in getAttachmentMetadata:", error)
    return null
  }
}
